import {css, CSSProperties, StyleSheet} from 'aphrodite';
import React from 'react';

import {Owner} from 'auth/PermissionConstants';
import {RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {EntitySummary} from 'constants/UserConstants';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import EntityNameTemplateField from 'name-templates/EntityNameTemplateField';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import SchemaNameInput from 'schemas/SchemaNameInput';
import SchemaOwnerDisplay from 'schemas/SchemaOwnerDisplay';
import SchemaWarehouseNameInput from 'schemas/SchemaWarehouseNameInput';
import {getItemTypeDisplayName, itemTypeToFolderItemTypeAndSequenceType} from 'schemas/TagSchemaItemTypeUtil';
import TagSchemaItemTypeSelector from 'settings/registry/TagSchemaItemTypeSelector';

import {SchemaInterface} from './SchemaInterfaceConstants';
import SchemaInterfacePrefixInput from './SchemaInterfacePrefixInput';
import {ClientInterfaceField} from './SchemaInterfaceUtils';
import {convertFromClientInterfaceFieldToSchemaField} from './SchemaInterfaceUtils';

interface SchemaInterfaceMetadataSectionProps {
  isEditableWarehouseTableName: boolean;
  hideWarehouseTableName?: boolean;
  name?: string;
  sqlIdentifier?: string | null;
  interface: SchemaInterface | null;
  ownerId?: string;
  ownerChoices?: Array<Owner> | null;
  itemType: TagSchemaItemType | null;
  canSetItemType: boolean;
  prefix?: string;
  nameTemplateParts?: Array<NameTemplatePart>;
  shouldOrderPartsBySequence?: boolean;
  labelingStrategies?: Array<RegistryLabelingStrategy>;
  fields?: Array<ClientInterfaceField>;
  onNameChange: (name: string) => void;
  onOwnerChange: (owner: EntitySummary) => void;
  onItemTypeChange: (itemType: TagSchemaItemType | null) => void;
  onSqlIdentifierChange: (sqlIdentifier: string) => void;
  onPrefixChange: (prefix: string) => void;
  onNameTemplateChange?: (
    nameTemplateParts: Array<NameTemplatePart>,
    shouldOrderPartsBySequence: boolean
  ) => void;
}

// exported for test only
const baseTestAttrs = {component: 'SchemaInterfaceMetadataSection'};
export const DATA_TEST_ATTRS = {
  name: {...baseTestAttrs, element: 'schemaInterfaceName'},
  itemType: {...baseTestAttrs, element: 'schemaInterfaceItemType'},
  owner: {...baseTestAttrs, element: 'ownerName'},
  warehouseTable: {...baseTestAttrs, element: 'warehouseTable'},
  prefix: {...baseTestAttrs, element: 'schemaInterfacePrefix'},
};

export default function SchemaInterfaceMetadataSection(
  props: SchemaInterfaceMetadataSectionProps
): JSX.Element {
  return (
    <>
      <div className={css(styles.schemaMetadataRow1)}>
        <div className={css(styles.schemaMetadataColumn1)}>
          <div className={css(styles.schemaMetadataField)}>
            <SchemaNameInput
              isWritable={true}
              name={props.name}
              onNameChange={props.onNameChange}
              schema={props.interface}
              dataTestAttrs={DATA_TEST_ATTRS.name}
            />
          </div>
        </div>
        <div className={css(styles.schemaMetadataColumn2)}>
          <div className={css(styles.schemaMetadataField)}>
            <SchemaOwnerDisplay
              ownerId={props.ownerId}
              ownerChoices={props.ownerChoices}
              onOwnerChange={props.onOwnerChange}
              schema={props.interface}
              dataTestAttrs={DATA_TEST_ATTRS.owner}
              ownerLabel="Organization"
            />
          </div>
        </div>
      </div>
      <div className={css(styles.schemaMetadataRow1)}>
        <div className={css(styles.schemaMetadataColumn1)}>
          {!props.hideWarehouseTableName && (
            <div className={css(styles.schemaMetadataField)}>
              <SchemaWarehouseNameInput
                isWritable={props.isEditableWarehouseTableName}
                schema={props.interface}
                sqlIdentifier={props.sqlIdentifier || ''}
                onSqlIdentifierChange={props.onSqlIdentifierChange}
                dataTestAttrs={DATA_TEST_ATTRS.warehouseTable}
              />
            </div>
          )}
        </div>
        <div className={css(styles.schemaMetadataColumn2)}>
          <div className={css(styles.schemaMetadataField)}>
            <TagSchemaItemTypeSelector
              displayName={props.itemType ? getItemTypeDisplayName(props.itemType) : 'None'}
              disabled={!props.canSetItemType}
              entityOptionConfiguration={{defaultOptionName: 'None'}}
              selectedItemType={props.itemType}
              onChangeItemType={props.onItemTypeChange}
              showLabel={true}
            />
          </div>
        </div>
      </div>
      <div className={css(styles.schemaMetadataRow2)}>
        <div className={css(styles.schemaMetadataColumn1)}>
          <div className={css(styles.schemaMetadataField)}>
            <SchemaInterfacePrefixInput
              interface={props.interface}
              isRequired={false}
              isWritable={true}
              prefix={props.prefix}
              onPrefixChange={props.onPrefixChange}
              dataTestAttrs={DATA_TEST_ATTRS.prefix}
            />
          </div>
        </div>
        {props.interface != null &&
          props.nameTemplateParts != null &&
          props.fields != null &&
          props.labelingStrategies != null &&
          props.onNameTemplateChange && (
            <div className={css(styles.schemaMetadataColumn2)}>
              <div className={css(styles.schemaMetadataField)}>
                <EntityNameTemplateField
                  nameTemplateParts={props.nameTemplateParts}
                  schemaFields={props.fields.map((field) =>
                    convertFromClientInterfaceFieldToSchemaField(field)
                  )}
                  schemaId={props.interface.id}
                  bioentityType={
                    props.itemType
                      ? itemTypeToFolderItemTypeAndSequenceType(props.itemType).folderItemType
                      : ''
                  }
                  sequenceType={
                    props.itemType
                      ? itemTypeToFolderItemTypeAndSequenceType(props.itemType).sequenceType
                      : null
                  }
                  isWritable={true}
                  setNameTemplateParts={props.onNameTemplateChange}
                  shouldOrderNamePartsBySequence={props.shouldOrderPartsBySequence ?? false}
                  labelingStrategies={props.labelingStrategies}
                  itemType={props.itemType}
                />
              </div>
            </div>
          )}
      </div>
    </>
  );
}

const rowStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
};

const styles = StyleSheet.create({
  ownerDropdown: {
    flex: 1,
  },
  schemaMetadataRow1: {
    ...rowStyles,
    marginBottom: '10px',
  },
  schemaMetadataRow2: {
    ...rowStyles,
    marginBottom: '32px',
  },
  schemaMetadataColumn1: {
    flex: 0.33,
    minWidth: 0,
  },
  schemaMetadataColumn2: {
    flex: 0.63,
    minWidth: 0,
  },
  schemaMetadataField: {
    width: 'auto',
    marginBottom: '0',
  },
});
